import React, {useMemo, useRef} from 'react';
import styles from './FormDialog.module.css';
import useFocusTrap from '../app-shell/useFocusTrap';
import {FormErrorSummary} from './form/FormLayout';

/**
 * A modal form: title, optional description, arbitrary field children, an
 * error summary, and a Cancel / submit action pair. Built on the same
 * scrim+trap shell as ConfirmDialog/RenameDialog, but for forms with more
 * than one field (add member, add assignee, configure Jira, create org).
 * Focus starts on the first field, Enter submits via the native <form>.
 */
const FormDialog = ({
    open, title, description, children, errors,
    onSubmit, onCancel, submitLabel = 'Save', submitDisabled, busy, busyLabel = 'Saving…',
    wide,
}) => {
    const dialogRef = useRef(null);
    const firstFieldRef = useRef(null);
    const initialFocusRefs = useMemo(() => [firstFieldRef], []);

    useFocusTrap(open, dialogRef, initialFocusRefs);

    if (!open) return null;

    return (
        <div className={styles.scrim} onClick={onCancel}>
            <form
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="form-dialog-title"
                className={`${styles.dialog} ${wide ? styles.wide : ''}`}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
                onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
            >
                <h3 id="form-dialog-title" className={styles.title}>{title}</h3>
                {description && <p className={styles.description}>{description}</p>}
                <FormErrorSummary errors={errors}/>
                <div className={styles.fields}>
                    {typeof children === 'function' ? children(firstFieldRef) : children}
                </div>
                <div className={styles.actions}>
                    <button type="button" className={styles.cancelButton} onClick={onCancel}>Cancel</button>
                    <button type="submit" className={styles.submitButton} disabled={busy || submitDisabled}>
                        {busy ? busyLabel : submitLabel}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FormDialog;
